import { useState, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line, ReferenceLine,
} from 'recharts'
import {
  AlertTriangle, ShieldAlert, Activity, Clock, Pause,
  Bell, FileText, RefreshCw, Settings2, Plus, X, Trash2,
} from 'lucide-react'
import clsx from 'clsx'
import { Card, MetricCard, Badge, PageHeader, EmptyState } from '../components/Card'
import ErrorBoundary from '../components/ErrorBoundary'
import { useToast } from '../components/Toast'
import { layout } from '../utils/layout'
import {
  crisisStatus,
  pauseAllContent,
  type CrisisStatusResponse,
} from '../api/client'

// ── Types ────────────────────────────────────────────────────────────────────

type CrisisLevel = 'normal' | 'watch' | 'warning' | 'critical' | 'emergency'

interface CrisisConfig {
  watch_threshold: number
  warning_threshold: number
  critical_threshold: number
  emergency_threshold: number
  keywords: string[]
}

// ── Crisis Level Config ──────────────────────────────────────────────────────

const CRISIS_LEVELS: Record<CrisisLevel, { label: string; color: string; bg: string; border: string; ring: string }> = {
  normal:    { label: 'Normal',    color: 'text-green-400',  bg: 'bg-green-900/30',  border: 'border-green-700', ring: 'ring-green-500'  },
  watch:     { label: 'Watch',     color: 'text-blue-400',   bg: 'bg-blue-900/30',   border: 'border-blue-700',  ring: 'ring-blue-500'   },
  warning:   { label: 'Warning',   color: 'text-yellow-400', bg: 'bg-yellow-900/30', border: 'border-yellow-700',ring: 'ring-yellow-500' },
  critical:  { label: 'Critical',  color: 'text-orange-400', bg: 'bg-orange-900/30', border: 'border-orange-700',ring: 'ring-orange-500' },
  emergency: { label: 'Emergency', color: 'text-red-400',    bg: 'bg-red-900/30',    border: 'border-red-700',   ring: 'ring-red-500'    },
}

const LEVEL_ORDER: CrisisLevel[] = ['normal', 'watch', 'warning', 'critical', 'emergency']

function toCrisisLevel(value: string): CrisisLevel {
  switch (value) {
    case 'normal':
    case 'watch':
    case 'warning':
    case 'critical':
    case 'emergency':
      return value
    default:
      return 'warning'
  }
}

// ── Loading Skeleton ─────────────────────────────────────────────────────────

function CrisisSkeleton() {
  return (
    <div className="p-6 space-y-6 animate-pulse" role="status" aria-label="Loading crisis data">
      <div className="h-32 bg-slate-800/50 rounded-xl" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-24 bg-slate-800/50 rounded-xl" />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="h-64 bg-slate-800/50 rounded-xl" />
        <div className="h-64 bg-slate-800/50 rounded-xl" />
      </div>
      <span className="sr-only">Loading crisis monitoring data...</span>
    </div>
  )
}

// ── Duration formatter ───────────────────────────────────────────────────────

function formatDuration(isoStart: string): string {
  const ms = Date.now() - new Date(isoStart).getTime()
  const hours = Math.floor(ms / 3600000)
  const minutes = Math.floor((ms % 3600000) / 60000)
  if (hours > 24) {
    const days = Math.floor(hours / 24)
    return `${days}d ${hours % 24}h`
  }
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
}

// ── Main View ────────────────────────────────────────────────────────────────

function CrisisViewContent() {
  const toast = useToast()
  const qc = useQueryClient()
  const [showConfig, setShowConfig] = useState(false)
  const [lastRefresh, setLastRefresh] = useState(new Date())
  const [newKeyword, setNewKeyword] = useState('')

  const { data: crisisData, isLoading, isError, refetch } = useQuery({
    queryKey: ['crisis', 'status'],
    queryFn: crisisStatus as () => Promise<CrisisStatusResponse>,
    refetchInterval: 10_000, // Auto-refresh every 10 seconds
  })

  const [config, setConfig] = useState<CrisisConfig>({
    watch_threshold: -10,
    warning_threshold: -20,
    critical_threshold: -35,
    emergency_threshold: -50,
    keywords: ['outage', 'scam', 'lawsuit', 'recall', 'boycott', 'hack', 'breach'],
  })

  // Track last refresh time
  useEffect(() => {
    if (crisisData) {
      setLastRefresh(new Date())
    }
  }, [crisisData])

  const pauseAllMut = useMutation({
    mutationFn: pauseAllContent,
    onSuccess: (data) => {
      toast.warning(`${data.message ?? 'All scheduled content has been paused'}`)
      void qc.invalidateQueries({ queryKey: ['drafts'] })
      void qc.invalidateQueries({ queryKey: ['calendar'] })
    },
  })

  const notifyTeamMut = useMutation({
    mutationFn: async () => {
      await new Promise(r => setTimeout(r, 300))
      return { ok: true }
    },
    onSuccess: () => toast.success('Crisis notification sent to team'),
  })

  const activateTemplateMut = useMutation({
    mutationFn: async () => {
      await new Promise(r => setTimeout(r, 400))
      return { ok: true }
    },
    onSuccess: () => toast.info('Crisis response template activated'),
  })

  const handleAddKeyword = useCallback(() => {
    const kw = newKeyword.trim().toLowerCase()
    if (kw && !config.keywords.includes(kw)) {
      setConfig(c => ({ ...c, keywords: [...c.keywords, kw] }))
      setNewKeyword('')
      toast.success(`Keyword "${kw}" added to watchlist`)
    }
  }, [newKeyword, config.keywords, toast])

  const handleRemoveKeyword = useCallback((kw: string) => {
    setConfig(c => ({ ...c, keywords: c.keywords.filter(k => k !== kw) }))
  }, [])

  if (isLoading) return <CrisisSkeleton />

  if (isError) {
    return (
      <div className={layout.pagePadding}>
        <Card>
          <EmptyState
            icon={<AlertTriangle className="w-12 h-12 text-red-400" />}
            title="Failed to load crisis data"
            description="Could not connect to the crisis monitoring service. Please try again."
            action={
              <button
                onClick={() => void refetch()}
                className="bg-brand-600 hover:bg-brand-700 text-white text-sm px-4 py-2 rounded-lg"
              >
                Retry
              </button>
            }
          />
        </Card>
      </div>
    )
  }

  const level = crisisData?.level ?? 'normal'
  const levelConfig = CRISIS_LEVELS[level]

  return (
    <div>
      <PageHeader
        title="Crisis Monitor"
        subtitle="Real-time brand safety and crisis detection"
        action={
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
            <button
              onClick={() => void refetch()}
              className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 transition-colors"
              aria-label="Refresh crisis data"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowConfig(c => !c)}
              className="flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 text-sm px-3 py-1.5 rounded-lg transition-colors"
              aria-label="Toggle configuration panel"
            >
              <Settings2 className="w-4 h-4" />
              Configure
            </button>
          </div>
        }
      />

      <div className={`${layout.pagePadding} ${layout.pageStack}`}>
        {/* Crisis Level Indicator */}
        <div
          className={clsx(
            'rounded-xl border-2 p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 transition-all',
            levelConfig.bg,
            levelConfig.border,
          )}
          role="status"
          aria-live="polite"
          aria-label={`Current crisis level: ${levelConfig.label}`}
        >
          <div className="flex items-center gap-3">
            <div className={clsx('p-2.5 sm:p-3 rounded-full shrink-0', levelConfig.bg, 'ring-2', levelConfig.ring)}>
              <ShieldAlert className={clsx('w-6 h-6 sm:w-8 sm:h-8', levelConfig.color)} aria-hidden="true" />
            </div>
            <div>
              <div className="text-xs text-slate-400 uppercase tracking-wider mb-0.5 sm:mb-1">Current Crisis Level</div>
              <div className={clsx('text-xl sm:text-2xl font-bold', levelConfig.color)}>
                {levelConfig.label}
              </div>
            </div>
          </div>

          {/* Level progression bar — desktop only */}
          <div className="hidden lg:flex items-center gap-1 flex-1 justify-center" role="progressbar" aria-label="Crisis level scale">
            {LEVEL_ORDER.map((l) => (
              <div
                key={l}
                className={clsx(
                  'w-16 h-2 rounded-full transition-all',
                  LEVEL_ORDER.indexOf(l) <= LEVEL_ORDER.indexOf(level)
                    ? clsx(CRISIS_LEVELS[l].bg, CRISIS_LEVELS[l].border, 'border')
                    : 'bg-slate-800',
                )}
                title={CRISIS_LEVELS[l].label}
              />
            ))}
          </div>

          {/* Quick actions */}
          <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
            <button
              onClick={() => pauseAllMut.mutate()}
              disabled={pauseAllMut.isPending}
              className="flex items-center gap-1.5 bg-red-700/60 hover:bg-red-700 text-red-100 text-sm px-3 py-2 rounded-lg transition-colors"
              aria-label="Pause all scheduled content"
            >
              <Pause className="w-4 h-4" />
              {pauseAllMut.isPending ? 'Pausing...' : 'Pause All Content'}
            </button>
            <button
              onClick={() => notifyTeamMut.mutate()}
              disabled={notifyTeamMut.isPending}
              className="flex items-center gap-1.5 bg-yellow-700/60 hover:bg-yellow-700 text-yellow-100 text-sm px-3 py-2 rounded-lg transition-colors"
              aria-label="Notify crisis team"
            >
              <Bell className="w-4 h-4" />
              {notifyTeamMut.isPending ? 'Sending...' : 'Notify Team'}
            </button>
            <button
              onClick={() => activateTemplateMut.mutate()}
              disabled={activateTemplateMut.isPending}
              className="flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 text-sm px-3 py-2 rounded-lg transition-colors"
              aria-label="Activate crisis response template"
            >
              <FileText className="w-4 h-4" />
              {activateTemplateMut.isPending ? 'Activating...' : 'Activate Template'}
            </button>
          </div>
        </div>

        {/* Metrics row */}
        <div className={`grid grid-cols-2 lg:grid-cols-4 ${layout.cardGap}`}>
          <MetricCard label="Active Crises" value={crisisData?.active_count ?? 0} />
          <MetricCard label="Events Today" value={crisisData?.events_today ?? 0} />
          <MetricCard
            label="Avg Sentiment"
            value={((crisisData?.avg_sentiment ?? 0) * 100).toFixed(0)}
            unit="%"
            change={-8.2}
          />
          <MetricCard label="Auto-Refresh" value="10s" />
        </div>

        {/* Active Crises + Sentiment Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Active Crises List */}
          <Card title="Active Crises">
            {!crisisData?.crises.length ? (
              <EmptyState
                icon={<Activity className="w-10 h-10" />}
                title="No active crises"
                description="All clear -- no active brand crises detected."
              />
            ) : (
              <div className="space-y-3">
                {crisisData.crises.map(crisis => {
                  const severity = toCrisisLevel(crisis.severity)
                  const sevConfig = CRISIS_LEVELS[severity]
                  return (
                    <div
                      key={crisis.id}
                      className={clsx(
                        'p-4 rounded-lg border transition-colors',
                        sevConfig.bg,
                        sevConfig.border,
                      )}
                      role="article"
                      aria-label={`Crisis: ${crisis.title}`}
                    >
                      <div className="flex items-start justify-between mb-2 gap-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <AlertTriangle className={clsx('w-4 h-4 shrink-0', sevConfig.color)} aria-hidden="true" />
                          <span className="text-sm font-semibold text-slate-200 truncate">{crisis.title}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge color={
                            severity === 'critical' || severity === 'emergency' ? 'red' :
                            severity === 'warning' ? 'yellow' : 'blue'
                          }>
                            {severity}
                          </Badge>
                        </div>
                      </div>
                      <p className="text-xs text-slate-400 mb-2">{crisis.description}</p>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Activity className="w-3 h-3" aria-hidden="true" />
                          {crisis.platform}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" aria-hidden="true" />
                          {formatDuration(crisis.started_at)}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </Card>

          {/* Sentiment Trend Chart */}
          <Card title="Sentiment Trend (30 days)">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={crisisData?.sentiment ?? []}
                  margin={{ top: 5, right: 10, left: -10, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: '#64748b' }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: '#64748b' }}
                    domain={[0, 1]}
                    tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`}
                  />
                  <Tooltip
                    contentStyle={{
                      background: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    formatter={(v: number) => [`${(v * 100).toFixed(1)}%`, '']}
                  />
                  <ReferenceLine
                    y={0.65}
                    stroke="#22c55e"
                    strokeDasharray="4 4"
                    label={{ value: 'Baseline', fill: '#22c55e', fontSize: 10 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="baseline"
                    stroke="#22c55e"
                    strokeWidth={1.5}
                    strokeDasharray="4 4"
                    dot={false}
                    name="Baseline"
                  />
                  <Line
                    type="monotone"
                    dataKey="current"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    dot={false}
                    name="Current"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* Event Timeline */}
        <Card title="Crisis Event Timeline (Last 30 Days)">
          {!crisisData?.timeline.length ? (
            <p className="text-sm text-slate-500 py-4 text-center">No events recorded.</p>
          ) : (
            <div className="relative">
              {/* Vertical timeline line — centered on the dot column (dot = 14px wide → center = 7px) */}
              <div className="absolute left-[7px] top-0 bottom-0 w-px bg-slate-700" aria-hidden="true" />
              <div className="space-y-4">
                {crisisData.timeline.map(event => {
                  const severity = toCrisisLevel(event.severity)
                  const sevConfig = CRISIS_LEVELS[severity]
                  const ts = new Date(event.timestamp)
                  return (
                    <div key={event.id} className="flex gap-4 relative" role="listitem">
                      {/* Timeline dot — 14px, z-10 so it paints over the line */}
                      <div
                        className={clsx(
                          'mt-1 w-3.5 h-3.5 rounded-full border-2 shrink-0 relative z-10',
                          sevConfig.bg,
                          sevConfig.border,
                        )}
                        aria-hidden="true"
                      />
                      <div className="flex-1 min-w-0 flex items-start justify-between gap-2 pb-1">
                        <div className="min-w-0">
                          <p className="text-sm text-slate-300">{event.message}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <Badge color={
                              severity === 'critical' || severity === 'emergency' ? 'red' :
                              severity === 'warning' ? 'yellow' : 'blue'
                            }>
                              {severity}
                            </Badge>
                            <span className="text-xs text-slate-500">{event.type.replace(/_/g, ' ')}</span>
                          </div>
                        </div>
                        <time className="text-xs text-slate-500 shrink-0" dateTime={event.timestamp}>
                          {ts.toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                          {' '}
                          {ts.toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}
                        </time>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </Card>

        {/* Configuration Panel */}
        {showConfig && (
          <Card
            title="Crisis Configuration"
            action={
              <button
                onClick={() => setShowConfig(false)}
                className="p-1 rounded hover:bg-slate-700 text-slate-400"
                aria-label="Close configuration panel"
              >
                <X className="w-4 h-4" />
              </button>
            }
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Threshold Settings */}
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-3">Sentiment Drop Thresholds</h4>
                <div className="space-y-3">
                  {([
                    { key: 'watch_threshold', label: 'Watch', level: 'watch' as CrisisLevel },
                    { key: 'warning_threshold', label: 'Warning', level: 'warning' as CrisisLevel },
                    { key: 'critical_threshold', label: 'Critical', level: 'critical' as CrisisLevel },
                    { key: 'emergency_threshold', label: 'Emergency', level: 'emergency' as CrisisLevel },
                  ] as const).map(({ key, label, level: l }) => (
                    <div key={key} className="flex items-center gap-3">
                      <label
                        className={clsx('text-xs w-16 sm:w-20 shrink-0', CRISIS_LEVELS[l].color)}
                        htmlFor={`threshold-${key}`}
                      >
                        {label}
                      </label>
                      <input
                        id={`threshold-${key}`}
                        type="range"
                        min="-80"
                        max="0"
                        value={config[key]}
                        onChange={(e) => setConfig(c => ({ ...c, [key]: Number(e.target.value) }))}
                        className="flex-1 accent-brand-500"
                        aria-label={`${label} threshold: ${config[key]}%`}
                      />
                      <span className="text-xs text-slate-400 w-10 sm:w-12 text-right shrink-0">{config[key]}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Keyword Watchlist */}
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-3">Keyword Watchlist</h4>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddKeyword() }}
                    placeholder="Add keyword..."
                    className="flex-1 bg-slate-800 border border-surface-border rounded-lg px-3 py-1.5 text-sm"
                    aria-label="New keyword to watch"
                  />
                  <button
                    onClick={handleAddKeyword}
                    disabled={!newKeyword.trim()}
                    className="flex items-center gap-1 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm px-3 py-1.5 rounded-lg transition-colors"
                    aria-label="Add keyword"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {config.keywords.map(kw => (
                    <span
                      key={kw}
                      className="inline-flex items-center gap-1 bg-slate-800 text-slate-300 text-xs px-2.5 py-1 rounded-full border border-slate-700"
                    >
                      {kw}
                      <button
                        onClick={() => handleRemoveKeyword(kw)}
                        className="p-0.5 rounded-full hover:bg-slate-600 transition-colors"
                        aria-label={`Remove keyword ${kw}`}
                      >
                        <Trash2 className="w-3 h-3 text-slate-500" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}

export default function CrisisView() {
  return (
    <ErrorBoundary>
      <CrisisViewContent />
    </ErrorBoundary>
  )
}
